import { Button, Container, Typography} from '@mui/material'
import { useTranslation } from "react-i18next"

import CommonBackButton from '../../../atoms/CommonBackButton/CommonBackButton'
import Spacer from "../../../atoms/Spacer/Spacer"

import { logLandingPageAction } from "../../../../clients/logger"
import useTrackPageView from "../../../../helpers/hooks/use-track-page-view"

import styles from './SubscriptionGiftDetailsForm.module.css'
import SubscriptionGiftStepperStep from "../../../molecules/SubscriptionGiftStepper/SubscriptionGiftStepper"
import useGoogleTagManager from "../../../../helpers/analytics/use-google-tag-manager"
import GtmEventName from "../../../../helpers/analytics/GtmEventName"
import CustomInput from '../../../atoms/CustomInput/CustomInput'
import { useNavigate, useParams } from "react-router-dom"
import moment from 'moment'
import CustomSelect, { CustomSelectOption } from '../../../atoms/CustomSelect/CustomSelect'
import { useContext, useEffect, useState } from 'react'
import SUBSCRIPTION_GIFT_FORM from '../../../../helpers/validation/rules/subscription-gift-form'
import { Route } from '../../../../router'
import { UserContext } from '../../../../stores/User/UserStore'
import { MembershipStatus } from '../../../../types/MembershipStatus'
import CustomDatePicker from '../../../atoms/CustomDatePicker/CustomDatePicker'

type CustomSelectValue = string | number | undefined

const SubscriptionGiftDetailsForm = () => {

    const { t } = useTranslation()
    const { trackButtonClick } = useGoogleTagManager()

    const { dob } = useParams()
    const navigate = useNavigate()
    const { state: userState } = useContext(UserContext)
    const [giftPeriod, setGiftPeriod] = useState<CustomSelectValue>(0)
    const dateOfBirthMoment = moment(dob, `DDMMYYYY`)
    const [myName, setMyName] = useState(``)
    const [myEmail, setMyEmail] = useState(``)
    const [senderDateOfBirth, setSenderDateOfBirth] = useState<Date | null>(null)
    const [recipientName, setRecipientName] = useState(``)
    const [recipientPhoneNumber, setRecipientPhoneNumber] = useState(``)
    const [validationError, setValidationError] = useState(false)
    const validationErrorMessage = `Please double check the form for errors`
    const isActiveMember = (userState.membership?.data?.status === MembershipStatus.ACTIVE || userState.membership?.data?.status === MembershipStatus.UNION_ACTIVE)

    useTrackPageView(`Subscription Gift Details Form`)

    useEffect(() => {
        if (isActiveMember){
            setMyName(userState.membership?.data?.fullName || ``)
            setMyEmail(userState.membership?.data?.emailAddress || ``)
            setSenderDateOfBirth(userState.membership?.data?.dateOfBirth || null)
        }
    }, [])

    const subscriptionPeriodOptions = () => {
        const options: CustomSelectOption[] = []
        if (!dob || !dateOfBirthMoment.isValid()) {
            logLandingPageAction(`User tried to access Subscription Gift Details Form without a valid DOB`, `myInfo`)
            navigate(Route.HOME)
            return []
        }
        const dateOfBirthCopy = moment(dateOfBirthMoment)
        // eslint-disable-next-line no-magic-numbers
        const userMaxAgeDate = dateOfBirthCopy.add(25, `years`)
        let maxNumberOfYears = userMaxAgeDate.diff(moment(), `years`)
        if (!moment().isSameOrAfter(dateOfBirthCopy.clone().add(maxNumberOfYears, `years`))) {
            maxNumberOfYears += 1
        }
        // eslint-disable-next-line no-magic-numbers
        if (maxNumberOfYears > 7){
            return []
        }
        const localCopy = [...Array(maxNumberOfYears).keys()].map(i => i + 1)
        localCopy.forEach((i) => options.push({ label: i.toString(), value: i, id: i.toString() }))
        return options
    }

    const handleSubmit = () => {
        if (areInputsValid()){
            setValidationError(false)
            trackButtonClick(GtmEventName.SUBSCRIPTION_GIFT_DETAILS_SUBMIT)
            logLandingPageAction(`User pressed next button on subscription gift details form`, `myInfo`)
            userState.subscriptionGift = {
                ...userState.subscriptionGift,
                subscriptionPeriod: Number(giftPeriod),
                myName,
                myEmail,
                senderDOB: moment(senderDateOfBirth).format(`DDMMYYYY`),
                recipientName,
                recipientPhoneNumber,
            }
            navigate(`${Route.SUBSCRIPTION_GIFT_CARD_SELECTION}`)
        }
        else {
            setValidationError(true)
        }
    }

    const areInputsValid = (): boolean => {
        try {
            SUBSCRIPTION_GIFT_FORM.myEmail.parse(myEmail)
            SUBSCRIPTION_GIFT_FORM.myName.parse(myName)
            SUBSCRIPTION_GIFT_FORM.recipientName.parse(recipientName)
            SUBSCRIPTION_GIFT_FORM.phoneNumber.parse(recipientPhoneNumber)
            return Number(giftPeriod) > 0
            && !!senderDateOfBirth
            && !!myName
            && !!recipientName
        } catch (error) {
            return false
        }
    }

    return (
        <Container>
            <CommonBackButton />
            <Spacer height={24} />
            <SubscriptionGiftStepperStep activeStep={1}/>
            <Spacer height={36} />
            <Typography variant="h3">{t(`subscription_gift_details_form_header`)}</Typography>
            <Spacer height={24} />
            <CustomSelect
                options={subscriptionPeriodOptions()}
                label={t(`subscription_gift_details_subscription_period`)}
                placeholder={t(`common_placeholder`)}
                onChange={setGiftPeriod}
                value={giftPeriod}
                isSignUp={false}/>
            <Spacer height={24} />
            <Typography variant="h3">{t(`subscription_gift_details_form_header_2`)}</Typography>
            <Spacer height={24} />
            <CustomInput
                fullWidth
                label={t(`name`)}
                onChange={setMyName}
                readOnly={isActiveMember}
                value={myName}
                validation={SUBSCRIPTION_GIFT_FORM.myName}
                isSignUp={false}
            />
            <CustomInput
                fullWidth
                label={t(`email`)}
                onChange={setMyEmail}
                readOnly={isActiveMember}
                value={myEmail}
                eagerChange
                validation={SUBSCRIPTION_GIFT_FORM.myEmail}
                isSignUp={false}
            />
            {!isActiveMember && <>
                <CustomDatePicker
                    fullWidth
                    label={t(`date_of_birth`)}
                    onChange={setSenderDateOfBirth}
                    value={senderDateOfBirth}/>
            </>
            }
            <Typography variant="h3">{t(`subscription_gift_details_form_header_3`)}</Typography>
            <CustomInput
                fullWidth
                label={t(`name`)}
                onChange={setRecipientName}
                value={recipientName}
                validation={SUBSCRIPTION_GIFT_FORM.recipientName}
                isSignUp={false}
            />
            <CustomInput
                fullWidth
                label={t(`sign_up_phone_number`)}
                onChange={setRecipientPhoneNumber}
                value={recipientPhoneNumber}
                eagerChange
                validation={SUBSCRIPTION_GIFT_FORM.phoneNumber}
                isSignUp={false}
            />
            <CustomInput
                fullWidth
                label={t(`sign_up_birthdate`)}
                readOnly
                value={dateOfBirthMoment.format(`DD MMMM YYYY`)}
                isSignUp={false}
            />
            {validationError && <Typography className={styles.validationError}>{validationErrorMessage}</Typography>}
            <Button variant='contained' onClick={handleSubmit} fullWidth>{t(`next`)}</Button>
        </Container>
    )
}

export default SubscriptionGiftDetailsForm